import React, { useCallback, useState } from "react"

import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@coval/ui"
import { Bookmark, Link as LinkIcon, Star, X } from "lucide-react"

import { SaveViewForm } from "@/components/saved-views/SaveViewForm"
import { ViewsList } from "@/components/saved-views/ViewsList"

import { useRunsSavedViewsState } from "../hooks/useRunsSavedViewsState"
import type {
  RunsSavedView,
  RunsSavedViewsTransportState,
} from "../types/savedViews"

/**
 * Saved-views dropdown for the runs page. Mirrors the monitoring dropdown's
 * structure using the shared `SaveViewForm` + `ViewsList` components.
 *
 * v1 features beyond monitoring:
 *  - Copy Link action per row (deep-link sharing via ?viewId=)
 *  - Default-view star toggle (per-user, via evals_userdefaultsavedview)
 *
 * Inline rename and delete-confirm come free from the shared SavedViewItem.
 */
interface RunsSavedViewsDropdownProps {
  savedViewsTransport: RunsSavedViewsTransportState
}

export const RunsSavedViewsDropdown: React.FC<RunsSavedViewsDropdownProps> = ({
  savedViewsTransport,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const { state, actions, data, mutations } =
    useRunsSavedViewsState(savedViewsTransport)

  const handleSaveFormCancel = useCallback(() => {
    actions.handleResetForm()
    setIsOpen(false)
  }, [actions])

  const handleStartEdit = useCallback(
    (viewId: string) => {
      actions.setEditingViewId(viewId)
    },
    [actions],
  )

  const handleCancelEdit = useCallback(() => {
    actions.setEditingViewId(null)
  }, [actions])

  const handleConfirmEdit = useCallback(
    async (viewId: string, name: string) => {
      if (name.trim()) {
        await actions.handleRenameView(viewId, name)
      }
    },
    [actions],
  )

  const handleStartDelete = useCallback(
    (viewId: string) => {
      actions.setConfirmDeleteId(viewId)
    },
    [actions],
  )

  const handleCancelDelete = useCallback(() => {
    actions.setConfirmDeleteId(null)
  }, [actions])

  const handleConfirmDelete = useCallback(
    async (viewId: string) => {
      await actions.handleDeleteView(viewId)
    },
    [actions],
  )

  const handleLoadView = useCallback(
    (view: RunsSavedView) => {
      actions.handleLoadView(view)
      setIsOpen(false)
    },
    [actions],
  )

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.info({
        title: "Link copied",
        description: "Share this URL to load the same view.",
        duration: 6000,
      })
    } catch {
      toast.danger({
        title: "Failed to copy link",
        duration: 6000,
      })
    }
  }, [])

  const handleToggleDefault = useCallback(async () => {
    if (!state.loadedViewId) {
      return
    }
    if (state.userDefaultViewUlid === state.loadedViewId) {
      await actions.handleClearDefault()
    } else {
      await actions.handleSetDefault(state.loadedViewId)
    }
  }, [state.loadedViewId, state.userDefaultViewUlid, actions])

  const loadedViewName = data.savedViews?.find(
    (v) => v.ulid === state.loadedViewId,
  )?.name

  const isDefault =
    state.loadedViewId !== null &&
    state.userDefaultViewUlid === state.loadedViewId

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        // Reset transient inline state so reopening the dropdown doesn't put
        // the user back into a stale rename or delete-confirm row.
        actions.setEditingViewId(null)
        actions.setConfirmDeleteId(null)
      }
      setIsOpen(nextOpen)
    },
    [actions],
  )

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          icon
          tooltip="Saved views"
          active={!!state.loadedViewId}
          className={cn("relative")}
        >
          <Bookmark />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="start"
        className="w-80 p-0 min-w-80"
        aria-label="Saved views"
      >
        <div className="w-full">
          {/* Active view banner */}
          {state.loadedViewId && (
            <div className="px-3 py-2.5 border-b border-coval-border">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Bookmark className="size-3.5 text-coval-border-active shrink-0" />
                  <span className="text-sm font-medium text-coval-text truncate">
                    {loadedViewName}
                  </span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    icon
                    tooltip={isDefault ? "Unset as default" : "Set as default"}
                    variant="ghost"
                    onClick={() => {
                      void handleToggleDefault()
                    }}
                    aria-label={
                      isDefault ? "Unset as default" : "Set as default"
                    }
                    className={cn(isDefault && "text-amber-500")}
                  >
                    <Star />
                  </Button>

                  <Button
                    icon
                    tooltip="Copy link to view"
                    variant="ghost"
                    onClick={() => {
                      void handleCopyLink()
                    }}
                  >
                    <LinkIcon />
                  </Button>

                  <Button
                    icon
                    variant="ghost"
                    onClick={actions.clearLoadedView}
                    tooltip="Clear view"
                  >
                    <X />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <SaveViewForm
            viewName={state.viewName}
            onViewNameChange={actions.setViewName}
            viewDescription={state.viewDescription}
            onViewDescriptionChange={actions.setViewDescription}
            isShared={state.isShared}
            onIsSharedChange={actions.setIsShared}
            onSave={() => {
              void actions.handleSaveCurrentView()
            }}
            onCancel={handleSaveFormCancel}
            isLoading={mutations.createView.isPending}
          />

          <ViewsList
            orgViews={data.groupedViews.orgViews}
            myViews={data.groupedViews.myViews}
            loadedViewId={state.loadedViewId}
            editingViewId={state.editingViewId}
            confirmDeleteId={state.confirmDeleteId}
            isLoadingViews={data.isLoadingViews}
            onLoadView={handleLoadView}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onConfirmEdit={handleConfirmEdit}
            onStartDelete={handleStartDelete}
            onCancelDelete={handleCancelDelete}
            onConfirmDelete={(viewId) => {
              void handleConfirmDelete(viewId)
            }}
            isUpdating={mutations.updateView.isPending}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
